import type { LenderYields, BalanceData, AprData, LoopPostTradeMetrics } from '../types'
import { noOpResult, buildLoopResult } from '../utils'
import {
  getOrganicYieldCollateralSwap,
  getRewardYieldCollateralSwap,
  getIntrinsicYieldCollateralSwap,
} from '../yield'

/**
 * Compute post-trade balance & APR metrics for a collateral swap.
 *
 * Withdraws `dollarIn` of one collateral asset and deposits `dollarOut` of another.
 */
export function computeCollateralSwapDeltas(
  dollarIn: number,
  dollarOut: number,
  yieldParamsIn: LenderYields,
  yieldParamsOut: LenderYields,
  balance: BalanceData,
  apr: AprData,
  ltvIn: number,
  collateralLtvIn: number,
  ltvOut: number,
  collateralLtvOut: number
): LoopPostTradeMetrics {
  if (dollarIn === 0 || dollarOut === 0) return noOpResult(balance, apr)

  const cashFlowDeposits = balance.deposits * apr.depositApr
  const cashFlowDebt = balance.debt * apr.borrowApr
  const rewardFlowDeposits = balance.deposits * (apr.rewardDepositApr ?? 0)
  const rewardFlowDebt = balance.debt * (apr.rewardBorrowApr ?? 0)
  const intrinsicFlowDeposits = balance.deposits * apr.intrinsicDepositApr
  const intrinsicFlowDebt = balance.debt * apr.intrinsicBorrowApr

  const newBalance = { ...balance }
  const newApr = { ...apr }

  newBalance.borrowDiscountedCollateral += ltvOut * dollarOut - ltvIn * dollarIn
  newBalance.deposits += dollarOut - dollarIn
  newBalance.collateral +=
    collateralLtvOut * dollarOut - collateralLtvIn * dollarIn
  newBalance.nav += dollarOut - dollarIn

  ;[newApr.borrowApr, newApr.depositApr, newApr.apr] =
    getOrganicYieldCollateralSwap(
      dollarIn, dollarOut,
      cashFlowDeposits, cashFlowDebt,
      balance.deposits, balance.debt,
      yieldParamsIn, yieldParamsOut
    )
  newApr.rewards = getRewardYieldCollateralSwap(
    dollarIn, dollarOut,
    rewardFlowDeposits, rewardFlowDebt,
    balance.deposits, balance.debt,
    yieldParamsIn, yieldParamsOut
  )
  ;[newApr.intrinsicBorrowApr, newApr.intrinsicDepositApr, newApr.intrinsicApr] =
    getIntrinsicYieldCollateralSwap(
      dollarIn, dollarOut,
      intrinsicFlowDeposits, intrinsicFlowDebt,
      balance.deposits, balance.debt,
      yieldParamsIn, yieldParamsOut
    )

  return buildLoopResult(balance, newBalance, apr, newApr)
}
